/** @format */

import * as RadixDialog from "@radix-ui/react-dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { a, useTransition } from "@react-spring/web";
import React from "react";
import { useOnKeyDown } from "../../../hooks/useOnKeyDown";
import { cn } from "../../../util/classNameConstructor";
import Button from "../../core/button/Button";
import type { DrawerState } from "./Drawer.types";

interface DrawerProps {
  drawerState: DrawerState;
  closeDrawer: () => void;
}

const Drawer: React.FC<DrawerProps> = ({ drawerState, closeDrawer }) => {
  const { isOpen, drawer } = drawerState;

  useOnKeyDown((e) => {
    if (e.code === "Escape" && drawer?.options?.onClose !== false) {
      closeDrawer();
    }
  });

  const drawerTransitions = useTransition(isOpen, {
    from: {
      x: drawer?.options?.side === "right" ? 100 : -100,
      opacity: 0,
    },
    enter: {
      x: 0,
      opacity: 1,
    },
    leave: {
      x: drawer?.options?.side === "right" ? 100 : -100,
      opacity: 0,
    },
    config: { tension: 300, friction: 30 },
  });

  if (!drawer) return null;

  const options = drawer.options || {};
  const side = options.side || "left";
  const width = options.width || 420;

  const handleClose = () => {
    if (options.onClose !== false) {
      if (typeof options.onClose === "function") {
        try {
          options.onClose();
        } catch {}
      }
      closeDrawer();
    }
  };

  return (
    <RadixDialog.Root open={isOpen} modal={false}>
      <RadixDialog.Portal>
        <RadixDialog.Content>
          <VisuallyHidden asChild>
            <RadixDialog.Title>{options.title || "Drawer"}</RadixDialog.Title>
          </VisuallyHidden>

          {/* Drawer */}
          {drawerTransitions(
            (style, item) =>
              item && (
                <a.div
                  style={style}
                  className={cn(
                    "fixed top-0 h-screen bg-white overflow-y-auto",
                    side === "left"
                      ? "left-0 shadow-[2px_0_8px_rgba(0,0,0,0.15)]"
                      : "right-0 shadow-[-2px_0_8px_rgba(0,0,0,0.15)]",
                  )}
                  style={{
                    ...style,
                    width: `${width}px`,
                    zIndex: options.zIndex || 10000,
                  }}
                >
                  <Button
                    leadingIcon="x-md"
                    variant="shapeless"
                    classNameWrapper="absolute top-3 right-3 z-10"
                    aria-label="Close drawer"
                    onClick={handleClose}
                  />
                  {React.cloneElement(drawer.element, {
                    closeDrawer,
                    drawerOptions: options,
                  })}
                </a.div>
              ),
          )}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
};

export default Drawer;
